import type {
  AddressResolveResult,
  ComparablesQuery,
  ComparablesResponse,
  UnitDetailDTO,
  UnitListRow,
  UnitsQuery,
  UnitsResponse
} from './types';
import * as mocks from './mocks';

// Single fetch entry point for the units list. Until the backend in
// [DEV-105](/DEV/issues/DEV-105) ships, this delegates to the mock layer; the
// real client over fetch will land behind the same signatures.

const useMock =
  typeof import.meta !== 'undefined'
    ? (import.meta.env?.VITE_USE_MOCK ?? '1') !== '0'
    : true;

export const isMockMode = (): boolean => useMock;

// Translate a frontend `UnitsQuery` into the snake_case query string that the
// backend `/api/listings` + `/api/listings/export` endpoints expect. Filters the
// FE exposes but the backend does not (yearMin/yearMax, hasBalcony, hasElevator,
// energyRating) are dropped silently — schema reconciliation tracked separately.
function listingsParamsFromQuery(query: UnitsQuery): URLSearchParams {
  const p = new URLSearchParams();
  if (query.q && query.q.length >= 2) p.set('search', query.q);
  if (query.postnummer?.length) p.set('postnummer', query.postnummer.join(','));
  if (query.status?.length) p.set('status', query.status.join(','));
  if (query.source?.length) p.set('portal', query.source.map((s) => s.toLowerCase()).join(','));
  if (query.propertyType?.length) p.set('property_type', query.propertyType.join(','));
  if (query.priceMin != null) p.set('rent_min', String(query.priceMin));
  if (query.priceMax != null) p.set('rent_max', String(query.priceMax));
  if (query.m2Min != null) p.set('area_min', String(query.m2Min));
  if (query.m2Max != null) p.set('area_max', String(query.m2Max));
  if (query.roomsMin != null) p.set('rooms_min', String(query.roomsMin));
  if (query.roomsMax != null) p.set('rooms_max', String(query.roomsMax));
  const sortMap: Partial<Record<NonNullable<UnitsQuery['sort']>, string>> = {
    address: 'address',
    postnummer: 'postnummer',
    rentKrPerMonth: 'monthly_rent_dkk',
    krPerSqm: 'price_per_sqm',
    m2: 'area_m2',
    rooms: 'rooms',
    lastSeen: 'last_seen_at'
  };
  const mappedSort = query.sort ? sortMap[query.sort] : undefined;
  if (mappedSort) p.set('sort', mappedSort);
  if (query.dir) p.set('dir', query.dir);
  return p;
}

export interface ExportContext {
  viewId?: string;
  sourceUrl?: string;
}

export function buildListingsExportUrl(query: UnitsQuery, ctx: ExportContext = {}): string {
  const p = listingsParamsFromQuery(query);
  if (ctx.viewId) p.set('view_id', ctx.viewId);
  if (ctx.sourceUrl) p.set('source_url', ctx.sourceUrl);
  return `/api/listings/export?${p.toString()}`;
}

// Listing-centric pivot (decided 2026-05-05): the operator's primary list
// view is now Listings, not BBR Units. Most listings have unit_id=NULL
// because the listing→Unit linker is gated off until the DAWA /adresser
// floor/door work lands. The /api/v1/units endpoint still exists for the
// "estimate by address" path; this fetcher hits /api/listings instead.
//
// The page's UI types haven't migrated yet (UnitListRow / UnitsResponse).
// We adapt the listing DTO into the UnitListRow shape at this boundary so
// the table renders without touching the row-renderer code. When the FE
// gets a proper ListingRow component, this adapter goes away.
interface BackendListingRow {
  id: string;
  url: string;
  status: string;
  first_seen_at: string;
  last_seen_at: string;
  portal_slug: string | null;
  portal_name: string | null;
  monthly_rent_dkk: number | null;
  rooms: number | null;
  area_m2: number | null;
  postal_code: string | null;
  city: string | null;
  address_raw: string | null;
  title: string | null;
  property_type: string | null;
  available_from: string | null;
  street_name: string | null;
  house_number: string | null;
  floor: string | null;
  door: string | null;
  energy_label: string | null;
  construction_year: number | null;
  price_per_sqm: number | null;
}

interface BackendListingsResponse {
  items: BackendListingRow[];
  total: number;
  page_size: number;
  next_cursor: string | null;
}

function _adaptListing(row: BackendListingRow): UnitListRow {
  // Prefer the Unit-side address fields when present (linked listings),
  // fall back to the snapshot's address_raw otherwise.
  const composedStreet = row.street_name
    ? row.house_number
      ? `${row.street_name} ${row.house_number}`
      : row.street_name
    : null;
  const composedAddress =
    composedStreet ??
    row.address_raw ??
    '';
  return {
    id: row.id,
    url: row.url,
    address: composedAddress,
    city: (row.city ?? '') as UnitListRow['city'],
    postnummer: row.postal_code ?? '',
    m2: row.area_m2,
    rooms: row.rooms,
    yearBuilt: row.construction_year,
    rentKrPerMonth: row.monthly_rent_dkk,
    krPerSqm: row.price_per_sqm,
    status: (row.status ?? 'active') as UnitListRow['status'],
    source: (row.portal_name ?? row.portal_slug ?? '') as UnitListRow['source'],
    lastSeen: row.last_seen_at,
    spark: []
  };
}

export async function fetchUnits(query: UnitsQuery): Promise<UnitsResponse> {
  if (useMock) return mocks.fetchUnits(query);
  // Translate the FE query into the /api/listings query shape.
  const p = listingsParamsFromQuery(query);
  // Cursor-based pagination on the listings endpoint; the FE table
  // currently uses page+pageSize so we offset-paginate via cursor by
  // computing the offset and re-encoding. For v1 we just request the
  // first page-size of the requested page index.
  const pageSize = (query as { pageSize?: number }).pageSize ?? 50;
  p.set('page_size', String(pageSize));
  const res = await fetch(`/api/listings?${p.toString()}`);
  if (!res.ok) throw new Error(`Failed to load listings: ${res.status}`);
  const body = (await res.json()) as BackendListingsResponse;
  const rows = body.items.map(_adaptListing);
  return {
    rows,
    total: body.total,
    filtered: body.total,
    page: (query as { page?: number }).page ?? 1,
    pageSize: body.page_size,
    lastSync: null
  };
}

export async function fetchUnit(id: string): Promise<UnitListRow | null> {
  if (useMock) return mocks.fetchUnit(id);
  const res = await fetch(`/api/v1/units/${encodeURIComponent(id)}`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`Failed to load unit: ${res.status}`);
  return (await res.json()) as UnitListRow;
}

export async function fetchUnitDetail(id: string): Promise<UnitDetailDTO | null> {
  if (useMock) return mocks.fetchUnitDetail(id);
  const res = await fetch(`/api/v1/units/${encodeURIComponent(id)}/detail`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`Failed to load unit detail: ${res.status}`);
  return (await res.json()) as UnitDetailDTO;
}

export async function fetchComparables(
  unitId: string,
  query: ComparablesQuery = {}
): Promise<ComparablesResponse | null> {
  if (useMock) return mocks.fetchComparables(unitId, query);
  const params = new URLSearchParams();
  query.excludeIds?.forEach((id) => params.append('excludeIds', id));
  const res = await fetch(
    `/api/v1/units/${encodeURIComponent(unitId)}/comparables?${params.toString()}`
  );
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`Failed to load comparables: ${res.status}`);
  return (await res.json()) as ComparablesResponse;
}

export async function resolveAddress(q: string): Promise<AddressResolveResult | null> {
  if (useMock) return mocks.resolveAddress(q);
  const res = await fetch(`/api/v1/address/resolve?q=${encodeURIComponent(q)}`);
  if (res.status === 404) return null;
  if (!res.ok) throw new Error(`Address resolve failed: ${res.status}`);
  return (await res.json()) as AddressResolveResult;
}
